import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Target, Eye, HeartHandshake, BadgeCheck } from "lucide-react";
import { aboutIntro, timeline, company, vizyonMisyon } from "../mock";
import { Reveal, PageHero, CountUp } from "../components/Shared";
import useSEO from "../hooks/useSEO";

const values = [
  {
    icon: Target,
    title: "Misyonumuz",
    text: vizyonMisyon.misyon,
  },
  {
    icon: Eye,
    title: "Vizyonumuz",
    text: vizyonMisyon.vizyon,
  },
  {
    icon: HeartHandshake,
    title: "Değerlerimiz",
    text: "Güven, dakiklik, şeffaflık ve müşteri memnuniyeti; 1983'ten bu yana tüm iş süreçlerimizin değişmez temelini oluşturur.",
  },
];

const Hakkimizda = () => {
  useSEO({
    title: "Hakkımızda",
    description:
      "Nikomedya Turizm'in 1983'ten bugüne uzanan hikayesi. Kocaeli'de personel ve öğrenci taşımacılığında 42 yılı aşkın tecrübe, ISO ve OHSAS belgeli hizmet.",
  });

  return (
    <div>
      <PageHero
        title="Hakkımızda"
        subtitle="Taşımacılıkta kalite ve güvenin sembolü — 1983'ten bugüne uzanan yolculuğumuz."
      />

      {/* Story */}
      <section className="py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <Reveal direction="left">
              <p className="section-label text-[#1e9cd7] text-xs font-bold uppercase mb-3">
                Hikayemiz
              </p>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#0d2b4f] leading-snug tracking-tight">
                {aboutIntro.title}
              </h2>
              {aboutIntro.paragraphs.map((p, i) => (
                <p
                  key={i}
                  className="mt-5 text-[#5b6b7d] leading-relaxed text-[15px]"
                >
                  {p}
                </p>
              ))}
              <div className="mt-7 bg-[#e8f5fc] border border-[#1e9cd7]/20 rounded-2xl p-6 flex items-start gap-4">
                <BadgeCheck size={24} className="text-[#1e9cd7] shrink-0 mt-0.5" />
                <p className="text-sm text-[#0d2b4f] leading-relaxed font-medium">
                  {aboutIntro.certificatesNote}{" "}
                  <Link
                    to="/kurumsal/kalite-politikamiz"
                    className="text-[#1e9cd7] font-bold hover:underline"
                  >
                    Belgelerimizi görüntüleyin →
                  </Link>
                </p>
              </div>
              <div className="mt-9 grid grid-cols-2 gap-4 max-w-sm">
                <div className="bg-[#f5f8fb] rounded-2xl p-5 text-center">
                  <div className="text-3xl font-extrabold text-[#1e9cd7]">
                    <CountUp end={42} suffix="+" />
                  </div>
                  <div className="text-xs text-[#5b6b7d] font-medium mt-1">
                    Yıllık Tecrübe
                  </div>
                </div>
                <div className="bg-[#f5f8fb] rounded-2xl p-5 text-center">
                  <div className="text-3xl font-extrabold text-[#1e9cd7]">
                    1983
                  </div>
                  <div className="text-xs text-[#5b6b7d] font-medium mt-1">
                    Kuruluş Yılı
                  </div>
                </div>
              </div>
            </Reveal>

            {/* Timeline */}
            <Reveal direction="right">
              <div className="relative pl-8 border-l-2 border-[#e8f5fc] space-y-10">
                {timeline.map((t) => (
                  <div key={t.year} className="timeline-item relative">
                    <span className="timeline-dot absolute -left-[41px] top-1 w-4 h-4 rounded-full bg-[#0d2b4f] border-4 border-[#e8f5fc]" />
                    <span className="inline-block bg-[#e8f5fc] text-[#1e9cd7] text-xs font-bold px-3.5 py-1.5 rounded-full">
                      {t.year}
                    </span>
                    <h3 className="mt-3 text-lg font-bold text-[#0d2b4f]">
                      {t.title}
                    </h3>
                    <p className="mt-2 text-sm text-[#5b6b7d] leading-relaxed">
                      {t.text}
                    </p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-[#f5f8fb]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-14">
              <p className="section-label text-[#1e9cd7] text-xs font-bold uppercase mb-3">
                Kurumsal
              </p>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#0d2b4f] leading-snug tracking-tight">
                Bizi Biz Yapan Değerler
              </h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 100}>
                <div className="feature-card h-full bg-white border border-slate-100 rounded-2xl p-8">
                  <div className="feature-icon w-14 h-14 rounded-xl bg-[#e8f5fc] text-[#1e9cd7] flex items-center justify-center">
                    <v.icon size={26} />
                  </div>
                  <h3 className="mt-5 text-xl font-bold text-[#0d2b4f]">
                    {v.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#5b6b7d]">
                    {v.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="mt-16 text-center">
              <p className="text-[#5b6b7d] mb-5">
                Hizmetlerimiz hakkında detaylı bilgi almak ister misiniz?
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <Link
                  to="/hizmetlerimiz"
                  className="btn-primary inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-sm"
                >
                  Hizmetlerimiz
                  <ArrowRight size={17} />
                </Link>
                <a
                  href={company.phoneHref}
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-sm border-2 border-[#0d2b4f] text-[#0d2b4f] hover:bg-[#0d2b4f] hover:text-white transition-colors duration-300"
                >
                  {company.phone}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Hakkimizda;
